import React, { useEffect, useRef, useState } from 'react';
import { KeyRound, X } from 'lucide-react';

// Motif d'un changement de roadmap ou de budget quand un rôle tient le domaine sur le projet : demandé avant
// d'enregistrer, gardé dans l'historique avec l'avant et l'après. Annuler n'enregistre rien.

const DOMAIN_LABELS = { roadmap: 'La roadmap', budget: 'Le budget' };

const ReasonDialog = ({ request, onAnswer }) => {
  const [text, setText] = useState('');
  const field = useRef(null);
  useEffect(() => {
    setText('');
    if (request) requestAnimationFrame(() => field.current?.focus());
  }, [request]);
  useEffect(() => {
    if (!request) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') onAnswer(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, onAnswer]);
  if (!request) return null;

  const submit = (event) => {
    event.preventDefault();
    const value = text.trim();
    if (value) onAnswer(value);
  };
  const domains = (request.domains || []).map((key) => DOMAIN_LABELS[key] || key);
  const held = domains.length > 1 ? `${domains[0]} et ${domains[1].toLowerCase()}` : domains[0] || 'La roadmap';

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center">
      <button type="button" aria-label="Annuler" onClick={() => onAnswer(null)} className="absolute inset-0 bg-slate-900/30" />
      <form onSubmit={submit} className="relative w-full max-w-md space-y-3 rounded-t-2xl bg-white p-4 shadow-2xl sm:rounded-2xl sm:p-5">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 rounded-lg bg-violet-50 p-1.5 text-violet-600"><KeyRound size={15} /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">{request.title}</p>
            {(request.lines || []).map((line) => (
              <p key={line} className="mt-0.5 text-xs text-slate-500">{line}</p>
            ))}
          </div>
          <button type="button" onClick={() => onAnswer(null)} aria-label="Fermer"
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={15} />
          </button>
        </div>
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Motif</span>
          <textarea ref={field} value={text} rows={3} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e); }}
            placeholder="Pourquoi ce changement ? Par exemple : le client décale la livraison du banc."
            className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50" />
        </label>
        <p className="text-[11px] leading-snug text-slate-400">
          {held} de ce projet {domains.length > 1 ? 'sont tenus' : 'est tenu'} par un rôle : le motif est gardé dans
          l'historique, avec l'avant et l'après.
        </p>
        <div className="flex justify-end gap-1.5">
          <button type="button" onClick={() => onAnswer(null)} className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100">
            Annuler
          </button>
          <button type="submit" disabled={!text.trim()}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40 ${request.danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-slate-900 hover:bg-slate-700'}`}>
            {request.confirm || 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReasonDialog;
